import type { PlatformTypeName } from "@multi-chat/shared";
import { Unplug } from "lucide-react";
import { ChannelMark } from "../channel/ChannelMark.js";
import { IsolatedBrands } from "./IsolatedBrands.js";
import { Button } from "../ui/button/index.js";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../ui/alert-dialog/index.js";

export function DisconnectConfirmPanel({
  platformType,
  title,
  description,
  cancelLabel,
  confirmLabel,
  pendingLabel,
  busy,
  onCancel,
  onConfirm,
}: {
  platformType: PlatformTypeName;
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel: string;
  pendingLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div className="grid gap-5" data-testid="disconnect-confirm-panel">
      <div className="grid justify-items-start gap-3">
        <span className="relative inline-grid">
          <ChannelMark platformType={platformType} size="md" shape="circle" />
          <span className="absolute -bottom-1 -end-1 inline-grid size-5 place-items-center rounded-full bg-surface-elevated text-destructive ring-1 ring-destructive/25">
            <Unplug className="size-3" aria-hidden="true" />
          </span>
        </span>
        <div className="grid gap-1.5">
          <h2 className="m-0 text-[1.0625rem] font-semibold tracking-[-0.01em] text-foreground">
            <IsolatedBrands text={title} />
          </h2>
          <p className="m-0 text-[0.8125rem] leading-relaxed text-muted">
            <IsolatedBrands text={description} />
          </p>
        </div>
      </div>
      <div className="grid gap-2 sm:flex sm:min-h-10 sm:flex-wrap sm:items-center sm:justify-end sm:gap-3">
        <Button
          type="button"
          variant="outline"
          className="w-full sm:w-auto"
          onClick={onCancel}
          disabled={busy}
        >
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant="destructive"
          className="w-full sm:w-auto"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? pendingLabel : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

export function ConfirmDisconnectDialog({
  open,
  onOpenChange,
  platformType,
  title,
  description,
  cancelLabel,
  confirmLabel,
  pendingLabel,
  busy,
  onConfirm,
  testId = "confirm-disconnect-dialog",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platformType: PlatformTypeName;
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel: string;
  pendingLabel: string;
  busy: boolean;
  onConfirm: () => void;
  testId?: string;
}): React.JSX.Element {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <div data-testid={testId}>
          <AlertDialogTitle className="sr-only">{title}</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">{description}</AlertDialogDescription>
          <DisconnectConfirmPanel
            platformType={platformType}
            title={title}
            description={description}
            cancelLabel={cancelLabel}
            confirmLabel={confirmLabel}
            pendingLabel={pendingLabel}
            busy={busy}
            onCancel={() => onOpenChange(false)}
            onConfirm={onConfirm}
          />
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
